import { useEffect, useState } from 'react';
import { X, Trophy, Star, Flame, Award } from 'lucide-react';
import type { Badge } from '../services/conceptPlus1Service';

interface BadgeNotificationProps {
  badge: Badge;
  onClose: () => void;
}

const BADGE_ICONS = {
  achievement: Trophy,
  mastery: Star,
  consistency: Flame,
  milestone: Award,
  performance: Trophy,
};

const BADGE_COLORS = {
  achievement: 'from-yellow-400 to-yellow-600',
  mastery: 'from-blue-400 to-blue-600',
  consistency: 'from-orange-400 to-orange-600',
  milestone: 'from-green-400 to-green-600',
  performance: 'from-purple-400 to-pink-500',
};

const BADGE_TEXT_COLORS = {
  achievement: 'text-yellow-500',
  mastery: 'text-blue-500',
  consistency: 'text-orange-500',
  milestone: 'text-green-500',
  performance: 'text-purple-600',
};

export function BadgeNotification({ badge, onClose }: BadgeNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState<number[]>([]);

  const Icon = BADGE_ICONS[badge.badge_type as keyof typeof BADGE_ICONS] ?? Trophy;
  const gradientColor = BADGE_COLORS[badge.badge_type as keyof typeof BADGE_COLORS] ?? 'from-yellow-400 to-yellow-600';
  const textColor = BADGE_TEXT_COLORS[badge.badge_type as keyof typeof BADGE_TEXT_COLORS] ?? 'text-yellow-500';

  const badgeName = badge.name || badge.badge_name || 'Badge Earned';
  const badgeDesc = badge.description || badge.badge_description || 'Great achievement!';

  useEffect(() => {
    setIsVisible(true);
    setConfettiPieces(Array.from({ length: 50 }, (_, i) => i));
    const timer = setTimeout(() => { handleClose(); }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      {confettiPieces.map((piece) => (
        <div
          key={piece}
          className="confetti-piece absolute"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-10%',
            width: `${Math.random() * 10 + 5}px`,
            height: `${Math.random() * 10 + 5}px`,
            backgroundColor: ['#FF8F1F', '#0A2342', '#FFD700', '#FF6B6B', '#4ECDC4'][Math.floor(Math.random() * 5)],
            animation: `confetti-fall ${Math.random() * 3 + 2}s linear forwards`,
            animationDelay: `${Math.random() * 0.5}s`,
            opacity: Math.random() * 0.7 + 0.3,
          }}
        />
      ))}

      <div
        className={`relative bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 transform transition-all duration-300 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition">
          <X className="w-5 h-5" />
        </button>
        <div className="flex justify-center mb-6">
          <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center shadow-lg animate-bounce`}>
            <Icon className="w-12 h-12 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-center text-ocean-deep mb-2">Badge Earned!</h2>
        <h3 className={`text-2xl font-semibold text-center ${textColor} mb-3`}>{badgeName}</h3>
        <p className="text-gray-600 text-center mb-6 leading-relaxed">{badgeDesc}</p>
        <button
          onClick={handleClose}
          className="w-full bg-gradient-to-r from-coral-warm to-orange-500 text-white font-semibold py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105"
        >
          Continue Learning
        </button>
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-300 rounded-full opacity-75 animate-ping" />
        <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-orange-300 rounded-full opacity-75 animate-ping" style={{ animationDelay: '0.5s' }} />
      </div>

      <style>{`
        @keyframes confetti-fall {
          to { transform: translateY(100vh) rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
